/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import MsDyn365, {
    getCatalogId,
    IComponent,
    IComponentProps,
    IGridSettings,
    IImageSettings,
    msdyn365Commerce,
    TelemetryEvent
} from '@msdyn365-commerce/core';
import { getCartState, ICartActionResult, ICartState } from '@msdyn365-commerce/global-state';
import {
    CartLineValidationResults,
    CartsDataActions,
    format,
    ProductAvailableQuantity,
    ProductDimension,
    ProductPrice,
    ProductsDataActions,
    ProductSearchCriteria,
    ProductType,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import { ErrorNotification, NotificationsManager } from '@msdyn365-commerce-modules/notifications-core';
import { ArrayExtensions, generateProductImageUrl, ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { getPayloadObject, getTelemetryAttributes, IPopupProps, ITelemetryContent, Popup } from '@msdyn365-commerce-modules/utilities';
import React, { useState } from 'react';
// @ts-ignore
import { Primary } from '@churchofjesuschrist/eden-buttons';

import {
    PriceComponent,
    IItemsAddedToCartResources,
    ItemsAddedToCartDialogComponent,
    ItemSuccessfullyAddedToCartNotification,
    MultiItemsSuccessfullyAddedToCartNotification,
    OrderDetailsProduct
} from '@msdyn365-commerce/components';

import { updateCartLineAttributeValues, updateLineDeliverySpecifications } from '../../../../helpers/utilities/cart-line-utilities';
import { getConfigureErrors } from '../../../../modules/lds-buybox/components/utilities/error-utilities';
//@ts-ignore
import { Error, Modal } from '@churchofjesuschrist/eden-alert';

// import { IItemsAddedToCartResources } from './items-added-to-cart';
// import { ItemsAddedToCartDialogComponent } from './items-added-to-cart-dialog-component';
// import { ItemSuccessfullyAddedToCartNotification } from './notifications/item-successfully-added-to-cart-notification';
// import { MultiItemsSuccessfullyAddedToCartNotification } from './notifications/multi-items-successfully-added-to-cart-notification';
// import { OrderDetailsProduct } from './product-model';

/**
 * Interface for add to cart resources.
 */
export interface IAddToCartResources {
    goToCartText: string;
    continueShoppingText: string;
    closeNotificationLabel: string;
    headerItemOneText: string;
    headerItemFormatText: string;
    headerLinesFormatText?: string;
    headerMessageText: string;
    freePriceText: string;
    originalPriceText: string;
    currentPriceText: string;
    addedQuantityText: string;
    buyAgainNotificationTitle?: string;
    buyAgainNotificationCloseAriaLabel?: string;
    validationErrorMessage?: string;
    oneErrorText?: string;
    multiErrorsText?: string;
}

/**
 * Interface for add to cart components props.
 */
export interface IAddToCartComponentProps extends IComponentProps<{ product: SimpleProduct; price?: ProductPrice } | undefined> {
    className?: string;
    addToCartText: string;
    validationErrorText?: string;
    stockErrorText?: string;
    shippingAvailable?: string;
    outOfStockText?: string;
    signInText?: string;
    disabled?: boolean;
    quantity?: number;
    navigationUrl?: string;
    productAvailability?: ProductAvailableQuantity;
    getSelectedProduct?: Promise<SimpleProduct | null>;
    isNavigationToCartPageDisabled?: boolean;
    shouldSkipSiteSettings?: boolean;
    addToCartArialLabel?: string;
    products?: {
        productId: number;
        quantity: number;
        catalogId?: number;
    }[];
    orderDetailsProducts?: OrderDetailsProduct[];
    hasAvailableProducts?: boolean;
    imageSettings?: IImageSettings;
    gridSettings?: IGridSettings;

    isLoading?: boolean;
    isUpdatingDimension?: boolean;
    isLoadingDeliveryOptions?: boolean;
    isUpdatingDeliveryOptions?: boolean;
    isAddServiceItemToCart?: boolean;
    isAddEmailDeliveryItemToCart?: boolean;
    isPriceKeyedIn?: boolean;
    customPriceAmount?: number;
    isOrderQuantityLimitsFeatureEnabled?: boolean;

    dialogStrings?: IAddToCartResources;

    telemetryContent?: ITelemetryContent;

    catalogId?: number;

    isCustomPriceSelected?: boolean;
    maximumKeyInPrice?: number;
    minimumKeyInPrice?: number;
    defaultMaximumKeyInPrice?: number;
    defaultMinimumKeyInPrice?: number;
    isProductQuantityLoading?: boolean;

    onAdd?(result: ICartActionResult): void;
    onError?(result: IAddToCartFailureResult): void;
    changeUpdatingDimension?(isUpdatingDimension: boolean): void;
    changeUpdatingDeliveryOptions?(isUpdatingDeliveryOptions: boolean): void;
    setSubscriptionError?(): void;
    subscription?: any;
    subscriptionAddress?: any;
    subscriptionUserCountry?: any;
    active?: boolean;
    errorCallback?: any;
    resources?: any;
    cart?: any;
    deliveryLocation?: any;
    storeSelectorStateManager?: any;
    max?: any;
    setQuantityError?: any;
}

/**
 * Cart action Failure reason type.
 */
export declare type ICartActionFailureReason =
    | 'EMPTYINPUT'
    | 'MISSINGDIMENSION'
    | 'OUTOFSTOCK'
    | 'CARTACTIONFAILED'
    | 'INVALIDCUSTOMAMOUNT';

/**
 * Interface for add to cart failure result.
 */
export interface IAddToCartFailureResult {
    failureReason: ICartActionFailureReason;

    stockLeft?: number;
    cartActionResult?: ICartActionResult;
    missingDimensions?: ProductDimension[];
}

/**
 * This setting defines the experience when a product is added to cart. Corresponds to the configuration in Fabrikam.
 */
export enum AddToCartBehavior {
    /**
     * Navigate to cart page.
     */
    goToCart = 'goToCart',

    /**
     * Show item added to cart popup.
     */
    showModal = 'showModal',

    /**
     * Show mini cart popup.
     */
    showMiniCart = 'showMiniCart',

    /**
     * Show item added to cart notification.
     */
    showNotification = 'showNotification',

    /**
     * Do nothing and stay on the page.
     */
    nothing = 'nothing'
}

const zero = 0;
const defaultQuantity = 1;

/**
 * The propagate result.
 * @param props -- The add to cart component props.
 * @param result -- The cart action result.
 */
const propagateResult = (props: IAddToCartComponentProps, result: ICartActionResult): void => {
    if (props.onAdd) {
        props.onAdd(result);
    }
};

/**
 * The propagate error.
 * @param props -- The add to cart component props.
 * @param result -- The add to cart failure result.
 */
const propagateError = (props: IAddToCartComponentProps, result: IAddToCartFailureResult): void => {
    if (props.onError) {
        props.onError(result);
    }
};

/**
 * Retrieves add to cart input from the component props.
 * @param props - Add to cart component props.
 * @returns Add to cart input.
 */
const getAddToCartInputFromProps = async (props: IAddToCartComponentProps) => {
    const products = props.orderDetailsProducts?.filter(product => !ObjectExtensions.isNullOrUndefined(product.simpleProduct));
    const input = products?.map(product => {
        return {
            product: product.simpleProduct!,
            count: ObjectExtensions.isNullOrUndefined(product.salesLine.Quantity) ? defaultQuantity : product.salesLine.Quantity,
            catalogId: product.salesLine.CatalogId
        };
    });

    if (ArrayExtensions.hasElements(input)) {
        return input;
    }

    if (ArrayExtensions.hasElements(props.products)) {
        const actionContext = props.context.actionContext;
        const apiSettings = actionContext.requestContext.apiSettings;

        const searchCriteriaInput: ProductSearchCriteria = {
            Context: {
                ChannelId: apiSettings.channelId,
                CatalogId: props.catalogId ?? getCatalogId(props.context.actionContext.requestContext)
            },
            IncludeAttributes: false,
            Ids: props.products.map(product => product.productId)
        };

        const searchResult = await ProductsDataActions.searchByCriteriaAsync({ callerContext: actionContext }, searchCriteriaInput);
        const productSearchResultsWithImages = searchResult.map(productSearchResult => {
            const newProductSearchResult = { ...productSearchResult };
            const newImageUrl = generateProductImageUrl(newProductSearchResult, apiSettings);

            if (newImageUrl) {
                newProductSearchResult.PrimaryImageUrl = newImageUrl;
            }

            return newProductSearchResult;
        });

        return props.products.map(item => {
            const productData = productSearchResultsWithImages.find(product => product.RecordId === item.productId);
            let simpleProduct: SimpleProduct;
            if (productData) {
                simpleProduct = {
                    ...productData,
                    ProductTypeValue: ProductType.Variant,
                    AdjustedPrice: productData.Price,
                    BasePrice: productData.BasePrice ?? productData.Price
                };
            } else {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Cast to simple product since only record id is available.
                simpleProduct = {
                    RecordId: item.productId
                } as SimpleProduct;
            }

            return {
                product: simpleProduct,
                count: item.quantity,
                catalogId: item.catalogId
            };
        });
    }

    if (props.data?.product) {
        return [
            {
                product: props.data.product,
                count: props.quantity ?? defaultQuantity,
                catalogId: props.catalogId
            }
        ];
    }

    return [];
};

/**
 * Add one item to cart when it is not buy again.
 * @param props -- The props.
 * @param setDisabled -- The set disable call back.
 * @param openModal -- The open modal call back.
 */
const addOneItemToCart = async (
    props: IAddToCartComponentProps,
    setDisabled: (disabled: boolean) => void,
    openModal: (opened: boolean) => void,
    setIsOpen?: (open: boolean) => void,
    stockError?: (open: boolean, message: string) => void,
    quantityError?: (open: boolean, message: string) => void
): Promise<void> => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- read config file.
    const addToCartBehavior = props.context.app.config.addToCartBehavior;
    const data = props.data;

    const addToCartInput = await getAddToCartInputFromProps(props);
    const product = addToCartInput[0].product;
    const quantity = addToCartInput[0].count;

    const cartState = await getCartState(props.context.actionContext);
    let existingProduct = cartState.cart.CartLines?.find((cartLine: any) => {
        return cartLine.ProductId === product.RecordId;
    });

    let addToCartResult: any;
    let copyQuantity = quantity;
    if (existingProduct) {
        let newQuantity = (copyQuantity += existingProduct.Quantity!);
        if (
            props.productAvailability &&
            props.productAvailability.AvailableQuantity &&
            newQuantity <= props.productAvailability.AvailableQuantity
        ) {
            addToCartResult = await cartState.updateCartLineQuantity({
                cartLineId: existingProduct.LineId!,
                newQuantity: newQuantity,
                additionalProperties: { isUsingDefaultOrderSettingsMax: props.isOrderQuantityLimitsFeatureEnabled }
            });
        } else {
            addToCartResult = { errorDetails: { LocalizedMessage: 'Stock Error' }, validationResults: [] };
        }
    } else {
        let DeliveryWarehouseId;
        if (props && props.deliveryLocation && props.deliveryLocation[0] && props.deliveryLocation[0].InventLocationId)
            DeliveryWarehouseId = {
                OrgUnitNumber: props.deliveryLocation[0].InventLocationId,
                InventoryLocationId: props.deliveryLocation[0].InventLocationId,
                Latitude: 0,
                Longitude: 0
            };
        addToCartResult = await cartState.addProductToCart({
            product,
            count: quantity,
            availableQuantity: props.productAvailability?.AvailableQuantity,
            additionalProperties: { orderQuantityLimitsFeatureIsEnabled: props.isOrderQuantityLimitsFeatureEnabled },
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- read config file.
            enableStockCheck: props.context.app.config.enableStockCheck,
            isPriceKeyedIn: props.isPriceKeyedIn,
            customPrice: props.isPriceKeyedIn ? props.customPriceAmount : undefined,
            isAddEmailDeliveryItemToCart: props.isAddEmailDeliveryItemToCart,
            shouldSkipSiteSettings: props.shouldSkipSiteSettings,
            catalogId: addToCartInput[0].catalogId,
            deliveryMode: '',
            location: DeliveryWarehouseId
        });
    }

    if (addToCartResult.status === 'SUCCESS') {
        if (props.subscription && props.subscriptionAddress) {
            const ctx = { callerContext: props.context.actionContext };

            let cartIndex: any;
            const cartLineId = cartState.cart.CartLines!.filter((cartLine: any, index: any) => {
                cartIndex = index;
                return cartLine.ExtensionProperties[0].Value?.BooleanValue === true;
            });

            if (props.subscriptionAddress.Street) {
                props.subscriptionAddress.Street = props.subscriptionAddress.Street.replace(/\n/g, '');
            }
            //@ts-ignore
            delete props.subscriptionAddress.SubscriptionAccountNumber;

            const addressToBePassedIn = {
                LineId: cartLineId[cartIndex].LineId!,
                DeliverySpecification: {
                    DeliveryModeId: (props.config && props.config.deliveryMode) || 'MAG',
                    DeliveryPreferenceTypeValue: 1,
                    DeliveryAddress: { ...props.subscriptionAddress }
                }
            };
            await updateCartLineAttributeValues(
                cartLineId[cartIndex].LineId!,
                'SubscriptionAccountNumber',
                props.subscription.subscriptionAccountNumber,
                props.context.actionContext,
                cartState.cart
            );
            await updateLineDeliverySpecifications(ctx, cartState.cart.Id, [addressToBePassedIn]);
        }
        if (props.dialogStrings && addToCartBehavior === AddToCartBehavior.showModal) {
            setDisabled(false);
            openModal(true);
        } else if (props.dialogStrings && addToCartBehavior === AddToCartBehavior.showNotification) {
            setDisabled(false);
            const notification = new ItemSuccessfullyAddedToCartNotification(
                props.context,
                props.dialogStrings,
                props.imageSettings,
                props.gridSettings,
                product,
                data?.price,
                quantity,
                props.navigationUrl,
                props.telemetryContent!,
                props.id,
                props.typeName
            );
            NotificationsManager.instance().addNotification(notification);
        } else if (
            MsDyn365.isBrowser &&
            props.navigationUrl &&
            !props.isNavigationToCartPageDisabled &&
            (addToCartBehavior === undefined || addToCartBehavior === AddToCartBehavior.goToCart)
        ) {
            window.location.assign(props.navigationUrl);
        } else {
            setDisabled(false);
        }
        propagateResult(props, addToCartResult);
    } else {
        if (
            setIsOpen &&
            addToCartResult &&
            addToCartResult.errorDetails &&
            addToCartResult.errorDetails &&
            addToCartResult.errorDetails.LocalizedMessage &&
            addToCartResult.validationResults &&
            !addToCartResult.validationResults[0]
        ) {
            if (
                addToCartResult.errorDetails.LocalizedMessage === 'A validation error occurred.' ||
                addToCartResult.errorDetails.LocalizedMessage === 'Error de validación.' ||
                addToCartResult.errorDetails.LocalizedMessage === `Une erreur de contrôle s'est produite.` ||
                addToCartResult.errorDetails.LocalizedMessage === `Errore di convalida.` ||
                addToCartResult.errorDetails.LocalizedMessage === `유효성 검사 오류가 발생했습니다.` ||
                addToCartResult.errorDetails.LocalizedMessage === `Erro na validação.` ||
                addToCartResult.errorDetails.LocalizedMessage === `Произошла ошибка проверки.` ||
                addToCartResult.errorDetails.LocalizedMessage === `Überprüfungsfehler` ||
                addToCartResult.errorDetails.LocalizedMessage === `検証エラーが発生しました。`
            ) {
                setIsOpen(true);
            }
        }
        if (
            stockError &&
            addToCartResult &&
            addToCartResult.errorDetails &&
            addToCartResult.errorDetails &&
            addToCartResult.errorDetails.LocalizedMessage &&
            addToCartResult.errorDetails.LocalizedMessage === 'Stock Error' &&
            addToCartResult.validationResults &&
            !addToCartResult.validationResults[0] &&
            props.productAvailability &&
            props.productAvailability.AvailableQuantity
        ) {
            stockError(true, JSON.stringify(props.productAvailability.AvailableQuantity));
        }
        if (
            props.setQuantityError &&
            addToCartResult &&
            addToCartResult.errorDetails &&
            addToCartResult.errorDetails &&
            addToCartResult.errorDetails.LocalizedMessage &&
            addToCartResult.validationResults &&
            addToCartResult.validationResults[0] &&
            // @ts-ignore
            addToCartResult.validationResults[0].LocalizedMessageParameters &&
            // @ts-ignore
            addToCartResult.validationResults[0].LocalizedMessageParameters[0]
        ) {
            // setQuantityError(true, result.errorDetails.LocalizedMessage);
            props.setQuantityError(
                true,
                // @ts-ignore
                format(props.resources.maxQuantityError, addToCartResult.validationResults[0].LocalizedMessageParameters[0])
            );
        }
        // console.log('addToCartResult', addToCartResult);
        // if (addToCartResult && addToCartResult.substatus === 'QUANTITYLIMITS') {
        //     console.log('addToCartResult', addToCartResult);
        //     props.setQuantityError(true, 'test');
        //     // quantityError(true, format(props.resources.maxQuantityError, props.max));
        // }
        NotificationsManager.instance().addNotification(
            new ErrorNotification(
                addToCartResult.errorDetails?.LocalizedMessage ?? 'Add to cart failed',
                props.dialogStrings?.closeNotificationLabel ?? ''
            )
        );

        propagateError(props, { failureReason: 'CARTACTIONFAILED', cartActionResult: addToCartResult });
        setDisabled(false);
    }
};

/**
 * Callback to handle success of adding to cart.
 * @param props - Add to cart component props.
 * @param setDisabled - Callback to update disabled state of the component.
 * @param setItemsAddedToCartDialogOpen - Callback to update disabled state of the component.
 * @param setErrorMessage - Callback to update error message state of the component.
 * @param cartState - Current cart state.
 * @param addToCartInput - Input used for adding to cart.
 * @param addToCartResult - Result of adding to cart.
 */
const handleAddItemsToCartSuccess = async (
    props: IAddToCartComponentProps,
    setDisabled: (disabled: boolean) => void,
    setItemsAddedToCartDialogOpen: (opened: boolean) => void,
    setErrorMessage: (message: string) => void,
    cartState: ICartState,
    addToCartInput: {
        product: SimpleProduct;
        count: number;
    }[],
    addToCartResult: ICartActionResult
) => {
    // Validate cart for line errors.
    const validationResult: CartLineValidationResults = await CartsDataActions.validateForCheckoutAsync(
        { callerContext: props.context.actionContext, bypassCache: 'none' },
        cartState.cart.Id,
        cartState.cart.Version
    );
    const errorCount = (validationResult.ValidationFailuresByCartLines ?? []).length;
    const singleErrorCount = 1;
    const errorText = errorCount === singleErrorCount ? props.dialogStrings?.oneErrorText : props.dialogStrings?.multiErrorsText;
    const errorMessage =
        errorCount > zero ? format(props.dialogStrings?.validationErrorMessage ?? '', errorCount.toString(), errorText) : '';

    setErrorMessage(errorMessage);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- read config file.
    const addToCartBehavior = props.context.app.config.addToCartBehavior;

    if (props.dialogStrings && addToCartBehavior === AddToCartBehavior.showModal) {
        setDisabled(false);
        setItemsAddedToCartDialogOpen(true);
    } else if (props.dialogStrings && addToCartBehavior === AddToCartBehavior.showNotification) {
        setDisabled(false);
        const itemsAddedResource: IItemsAddedToCartResources = {
            viewCartButtonText: props.dialogStrings.goToCartText,
            closeButtonLabel: props.dialogStrings.closeNotificationLabel,
            itemsAddedToCartHeaderText: props.dialogStrings.headerMessageText,
            itemsAddedToCartFormatText: props.dialogStrings.headerItemFormatText,
            linesAddedToCartFormatText: props.dialogStrings.headerLinesFormatText ?? '{0}',
            itemsAddedValidationErrorMessage: errorMessage
        };

        const notification = new MultiItemsSuccessfullyAddedToCartNotification(
            props.context,
            itemsAddedResource,
            props.imageSettings,
            props.gridSettings,
            addToCartInput,
            props.navigationUrl,
            props.telemetryContent!,
            props.id,
            props.typeName
        );
        NotificationsManager.instance().addNotification(notification);
    } else if (
        MsDyn365.isBrowser &&
        props.navigationUrl &&
        !props.isNavigationToCartPageDisabled &&
        (addToCartBehavior === undefined || addToCartBehavior === AddToCartBehavior.goToCart)
    ) {
        window.location.assign(props.navigationUrl);
    } else {
        setDisabled(false);
    }
    propagateResult(props, addToCartResult);
};

/**
 * Add one or multi item to cart from buy again.
 * @param props -- The props.
 * @param setDisabled -- The set disable call back.
 * @param setItemsAddedToCartDialogOpen -- The open modal call back.
 * @param setErrorMessage -- The set error message call back.
 */
const addItemsToCart = async (
    props: IAddToCartComponentProps,
    setDisabled: (disabled: boolean) => void,
    setItemsAddedToCartDialogOpen: (opened: boolean) => void,
    setErrorMessage: (message: string) => void
): Promise<void> => {
    const addToCartInput = await getAddToCartInputFromProps(props);

    if (!ArrayExtensions.hasElements(addToCartInput)) {
        return;
    }

    const cartState = await getCartState(props.context.actionContext);
    const addToCartResult = await cartState.addProductsToCart(addToCartInput);

    if (addToCartResult.status === 'SUCCESS') {
        await handleAddItemsToCartSuccess(
            props,
            setDisabled,
            setItemsAddedToCartDialogOpen,
            setErrorMessage,
            cartState,
            addToCartInput,
            addToCartResult
        );
    } else {
        if (props.dialogStrings?.buyAgainNotificationTitle) {
            // For buy again show simple error notification.
            NotificationsManager.instance().addNotification(
                new ErrorNotification(
                    props.dialogStrings.buyAgainNotificationTitle,
                    props.dialogStrings.buyAgainNotificationCloseAriaLabel ?? ''
                )
            );
        } else {
            NotificationsManager.instance().addNotification(
                new ErrorNotification(
                    addToCartResult.errorDetails?.LocalizedMessage ?? 'Add to cart failed',
                    props.dialogStrings?.closeNotificationLabel ?? ''
                )
            );
        }

        propagateError(props, { failureReason: 'CARTACTIONFAILED', cartActionResult: addToCartResult });
        setDisabled(false);
    }
};

/**
 * Interface for add to cart component.
 */
export interface IAddtoCartComponent extends IComponent<IAddToCartComponentProps> {
    onClick(): (event: React.MouseEvent<HTMLElement>, props: IAddToCartComponentProps) => void;
}

/**
 * On click function.
 * @param _event - The mouse event.
 * @param props - The props.
 * @param setDisabled - Flag to define whether the element is disabled.
 * @param openModal - Flag to specify if it should open in a modal window.
 * @param setItemsAddedToCartDialogOpen - Sets items added to cart while dialog is open.
 * @param setErrorMessage - Error message.
 */
const onClick = async (
    _event: React.MouseEvent<HTMLElement>,
    props: IAddToCartComponentProps,
    setDisabled: (disabled: boolean) => void,
    openModal: (opened: boolean) => void,
    setItemsAddedToCartDialogOpen: (opened: boolean) => void,
    setErrorMessage: (message: string) => void,
    errorCallback: any,
    cart: any,
    setIsOpen: (open: boolean) => void,
    stockError?: (open: boolean, message: string) => void,
    quantityError?: (open: boolean, message: string) => void
): Promise<boolean> => {
    if (!ArrayExtensions.hasElements(props.products)) {
        const cartError = addToCartError(props);

        if (cartError) {
            propagateError(props, cartError);
            return false;
        }

        // setDisabled(true);
    }
    let itemAdded: boolean = false;
    const hasOrderDetailsProducts =
        ArrayExtensions.hasElements(props.orderDetailsProducts) && props.orderDetailsProducts.length > defaultQuantity;
    const hasProducts = ArrayExtensions.hasElements(props.products) && props.products.length > defaultQuantity;

    const hasMultipleProducts = hasOrderDetailsProducts || hasProducts;

    if (cart && cart.cart && cart.cart.CartLines && cart.cart.CartLines[0]) {
        if (
            // @ts-ignore
            cart.cart.CartLines[0].DeliveryMode === 'PICKUP' ||
            // @ts-ignore
            cart.cart.CartLines[0].DeliveryMode === 'Pickup' ||
            // @ts-ignore
            cart.cart.CartLines[0].DeliveryMode === 'Curbside' ||
            // @ts-ignore
            cart.cart.CartLines[0].DeliveryMode === 'CURBSIDE'
        ) {
            errorCallback({
                errorHost: 'ADDTOCART',
                configureErrors: getConfigureErrors(
                    undefined,
                    props.resources,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    props.resources.pickupCartError
                )
            });
            itemAdded = false;
        } else {
            if (props.shouldSkipSiteSettings && hasMultipleProducts) {
                await addItemsToCart(props, setDisabled, setItemsAddedToCartDialogOpen, setErrorMessage);
            } else {
                await addOneItemToCart(props, setDisabled, openModal, setIsOpen, stockError, quantityError);
            }
            errorCallback({
                errorHost: 'ADDTOCART',
                configureErrors: getConfigureErrors(undefined, props.resources, undefined, undefined, undefined, undefined, false)
            });
            itemAdded = true;
        }
    } else {
        if (props.shouldSkipSiteSettings && hasMultipleProducts) {
            await addItemsToCart(props, setDisabled, setItemsAddedToCartDialogOpen, setErrorMessage);
        } else {
            await addOneItemToCart(props, setDisabled, openModal, setIsOpen, stockError, quantityError);
        }
        itemAdded = true;
    }
    return itemAdded;
};

/**
 * Add to cart component action constant.
 */
const AddToCartComponentActions = {
    onClick
};

/**
 * Add to cart component.
 * @param props - The props.
 * @returns - The add to cart component.
 */
export const AddToCartFunctionalComponent: React.FC<IAddToCartComponentProps> = (props: IAddToCartComponentProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [stockErrorOpen, setStockErrorOpen] = useState(false);
    // const [quantityErrorOpen, setQuantityErrorOpen] = useState(false);
    const [stockErrorMessage, setStockErrorMessage] = useState('');
    // const [quantityErrorMessage, setQuantityErrorMessage] = useState('');
    const [disabled, setDisabled] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [isItemsAddedToCartDialogOpen, setItemsAddedToCartDialogOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [addToCartInput, setAddToCartInput] = useState<
        {
            product: SimpleProduct;
            count: number;
        }[]
    >([]);
    const stockError = (isVisible: boolean, message: string) => {
        setStockErrorOpen(isVisible);
        setStockErrorMessage(message);
    };
    // const quantityError = (isVisible: boolean, message: string) => {
    //     setQuantityErrorOpen(isVisible);
    //     setQuantityErrorMessage(message);
    // };
    const onClickHandler = async (event: React.MouseEvent<HTMLElement>) => {
        if (props.subscription && props.subscriptionAddress && props.subscriptionUserCountry && props.setSubscriptionError) {
            if (props.subscription.CountryRegionId !== props.subscriptionUserCountry) {
                return props.setSubscriptionError();
            }
        }
        // console.log(props.context.request.telemetryData.activeCart?.CartLines[0].DeliveryMode);
        // props.errorCallback({
        //     errorHost: 'FINDINSTORE',
        //     configureErrors: getConfigureErrors(undefined, props.resources, undefined, undefined, undefined, undefined, 'shipping')
        // });
        // Adobe Analytics for Add To Cart
        const { Name, Dimensions, ItemId } = addToCartInput[0].product;

        const language = Dimensions?.filter((dimension: any) => {
            return dimension.DimensionTypeValue === 4;
        });
        const Style = Dimensions?.filter((dimension: any) => {
            return dimension.DimensionTypeValue === 1;
        });
        const Size = Dimensions?.filter((dimension: any) => {
            return dimension.DimensionTypeValue === 3;
        });
        let currentCategories = sessionStorage.getItem('ProductCategory');
        let categories = currentCategories?.split(',');

        const addToCart = await AddToCartComponentActions.onClick(
            event,
            props,
            setDisabled,
            setModalOpen,
            setItemsAddedToCartDialogOpen,
            setErrorMessage,
            props.errorCallback,
            props.cart,
            setIsOpen,
            stockError
            // quantityError
        );

        let productSku = sessionStorage.getItem('productSku');

        if (addToCart === true) {
            // @ts-ignore
            if (window && window['digitalData']) {
                // @ts-ignore
                window['digitalData'].push({
                    event: 'Add to Cart',
                    product: [
                        {
                            info: {
                                sku: productSku,
                                id: ItemId,
                                name: Name,
                                language: language && language[0] && language[0].DimensionValue ? language[0].DimensionValue.Value : '',
                                style: Style && Style[0] && Style[0].DimensionValue ? Style[0].DimensionValue.Value : '',
                                size: Size && Size[0] && Size[0].DimensionValue ? Size[0].DimensionValue.Value : '',
                                categories: categories
                            },
                            quantity: addToCartInput[0].count
                        }
                    ]
                });
            }
        }
    };

    /**
     * Close dialog.
     */
    const closeItemsAddedToCartDialog = React.useCallback(() => {
        setItemsAddedToCartDialogOpen(false);
    }, []);

    const priceComponent = props.data?.price ? (
        <PriceComponent
            data={{ price: props.data.price }}
            context={props.context}
            id={props.id}
            typeName={props.typeName}
            freePriceText={props.dialogStrings?.freePriceText}
            originalPriceText={props.dialogStrings?.originalPriceText}
            currentPriceText={props.dialogStrings?.currentPriceText}
        />
    ) : (
        ''
    );

    const popupProps: IPopupProps = {
        context: props.context,
        className: 'msc-add-to-cart',
        id: props.id,
        typeName: props.typeName,
        data: { product: props.data?.product, price: props.data?.price },
        dialogStrings: props.dialogStrings,
        imageSettings: props.imageSettings,
        gridSettings: props.context.request.gridSettings,
        productQuantity: props.quantity !== undefined ? props.quantity : defaultQuantity,
        priceComponent,
        navigationUrl: props.navigationUrl,
        modalOpen,
        setModalOpen,
        telemetryContent: props.telemetryContent
    };

    const itemsAddedResource: IItemsAddedToCartResources = {
        viewCartButtonText: props.dialogStrings?.goToCartText ?? '',
        closeButtonLabel: props.dialogStrings?.closeNotificationLabel ?? 'Close',
        itemsAddedToCartHeaderText: props.dialogStrings?.headerMessageText ?? '',
        itemsAddedToCartFormatText: props.dialogStrings?.headerItemFormatText ?? '',
        linesAddedToCartFormatText: props.dialogStrings?.headerLinesFormatText ?? '{0}',
        itemsAddedValidationErrorMessage: errorMessage
    };

    const renderModalPopup = <Popup {...popupProps} />;
    const label = getLinkText(props);
    const payload = getPayloadObject(TelemetryEvent.AddToCart, props.telemetryContent!, label, '');
    const attributes = getTelemetryAttributes(props.telemetryContent!, payload);
    // const buttonClassName = props.shouldSkipSiteSettings ? '' : 'msc-add-to-cart ';
    let isLoadingData;

    React.useEffect(() => {
        /**
         * Retrieves add to cart input and updates the state.
         */
        const retrieveAddToCartInput = async () => {
            const retrievedInput = await getAddToCartInputFromProps(props);
            setAddToCartInput(retrievedInput);
        };

        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- Call async method as per the documentation of react useEffect.
        retrieveAddToCartInput();
    }, [props, setAddToCartInput]);
    if (props.data?.product.Dimensions) {
        if (props.data.product.Dimensions.find((dimension: any) => !(dimension.DimensionValue && dimension.DimensionValue.Value))) {
            // At least one dimension with no value exists on the product, so also not in intermediate state
            isLoadingData = false;
        }
    }
    if (!props.isLoading && !props.isUpdatingDimension && !props.isLoadingDeliveryOptions && !props.isUpdatingDeliveryOptions) {
        isLoadingData = false;
    } else {
        isLoadingData = true;
    }

    return (
        <>
            {renderModalPopup}
            {isOpen && (
                <Modal open={isOpen}>
                    <Error onClose={() => setIsOpen(false)}>
                        <div style={{ marginTop: '3px' }}>{props.validationErrorText}</div>
                    </Error>
                </Modal>
            )}
            {stockErrorOpen && props.stockErrorText && (
                <Modal open={stockErrorOpen}>
                    <Error onClose={() => setStockErrorOpen(false)}>
                        <div style={{ marginTop: '3px' }}>{props.stockErrorText.replace('{numLeft}', stockErrorMessage)}</div>
                    </Error>
                </Modal>
            )}
            {/* {quantityErrorOpen && props.stockErrorText && (
                <Modal open={quantityErrorOpen}>
                    <Error onClose={() => setQuantityErrorOpen(false)}>
                        <div style={{ marginTop: '3px' }}>{quantityErrorMessage}</div>
                    </Error>
                </Modal>
            )} */}

            {/* {quantityErrorOpen && (
                <Error onClose={() => setQuantityErrorOpen(false)}>
                    <div style={{ marginTop: '3px' }}>{quantityErrorMessage}</div>
                </Error>
            )} */}

            <Primary
                // className={classnames(buttonClassName, props.className)}
                className='buyBox-add-to-cart'
                aria-label={props.addToCartArialLabel ?? label}
                title={props.addToCartArialLabel ?? label}
                {...attributes}
                onClick={
                    props.context.request.user.isAuthenticated
                        ? onClickHandler
                        : () => {
                              window.location.replace(`${props.context.request.user.signInUrl}`);
                          }
                }
                disabled={props.disabled || disabled || isIntermediateState(props) || shouldShowOutOfStock(props, false) || isLoadingData}
                style={{ backgroundImage: shouldShowOutOfStock(props, false) ? 'initial' : '', minWidth: '215.84px' }}
                // onMouseEnter={() => props.mouseEnter()}
                // onMouseLeave={() => props.mouseLeave()}
            >
                {props.context.request.user.isAuthenticated ? props.shippingAvailable : props.signInText}
            </Primary>
            <ItemsAddedToCartDialogComponent
                className='msc-lines-added-to-cart-dialog'
                id={props.id}
                typeName={props.typeName}
                context={props.context}
                dialogStrings={itemsAddedResource}
                data={{
                    products: addToCartInput
                }}
                isModalOpen={isItemsAddedToCartDialogOpen}
                onClose={closeItemsAddedToCartDialog}
            />
        </>
    );
};

// Set default props
AddToCartFunctionalComponent.defaultProps = {
    quantity: defaultQuantity
};

/**
 * Resolves whether product is in stock.
 * @param props - The add to cart component props.
 * @returns Gets the link text.
 */
const getLinkText = (props: IAddToCartComponentProps): string => {
    return shouldShowOutOfStock(props, false) && props.outOfStockText ? props.outOfStockText : props.addToCartText;
};

/**
 * Resolves whether product is in stock.
 * @param props - The add to cart component props.
 * @returns The add to cart failure result.
 */
const addToCartError = (props: IAddToCartComponentProps): IAddToCartFailureResult | undefined => {
    const {
        data,
        productAvailability,
        isCustomPriceSelected,
        customPriceAmount,
        maximumKeyInPrice,
        minimumKeyInPrice,
        defaultMaximumKeyInPrice = 100,
        defaultMinimumKeyInPrice = 10
    } = props;

    if (!data || !data.product.RecordId) {
        // No product exists, won't be able to add to cart
        return { failureReason: 'EMPTYINPUT' };
    }

    if (data.product.Dimensions) {
        const missingDimensions = data.product.Dimensions.filter(
            dimension => !(dimension.DimensionValue && dimension.DimensionValue.Value)
        );

        if (ArrayExtensions.hasElements(missingDimensions)) {
            // At least one dimension with no value exists on the product, won't be able to add to cart
            return { failureReason: 'MISSINGDIMENSION', missingDimensions };
        }
    }

    if (shouldShowOutOfStock(props, true)) {
        const defaultAvailableQuantity = 0; // Showing as out of stock if no available products found.
        const availableQuantity = productAvailability?.AvailableQuantity ?? defaultAvailableQuantity;
        const stockLeft = Math.max(availableQuantity, defaultAvailableQuantity);

        return { failureReason: 'OUTOFSTOCK', stockLeft };
    }

    // When Custom price is selected, if there is no keyed-in price or keyed-in price is out of limit, should return error.
    if (
        isCustomPriceSelected &&
        (!customPriceAmount ||
            customPriceAmount > (maximumKeyInPrice || defaultMaximumKeyInPrice) ||
            customPriceAmount < (minimumKeyInPrice || defaultMinimumKeyInPrice))
    ) {
        return { failureReason: 'INVALIDCUSTOMAMOUNT' };
    }

    // Only allow adding to cart if not showing out of stock
    return undefined;
};

/**
 * Resolves whether product is in stock.
 * @param props - The add to cart component props.
 * @param includeCurrentQuantity - Flag to specify whether current quantity should be included.
 * @returns The dialog element.
 */
const shouldShowOutOfStock = (props: IAddToCartComponentProps, includeCurrentQuantity: boolean): boolean => {
    if (props.context.app.config.enableStockCheck === undefined || props.context.app.config.enableStockCheck === false) {
        return false;
    }

    // When skip site settings do not need show out of stock on adding to cart
    if (props.shouldSkipSiteSettings) {
        return false;
    }

    if (
        props.isLoading ||
        props.isProductQuantityLoading ||
        props.isUpdatingDimension ||
        props.isLoadingDeliveryOptions ||
        props.isUpdatingDeliveryOptions ||
        props.isAddServiceItemToCart
    ) {
        // Out of stock turn off, don't bother showing out of stock
        return false;
    }

    if (!props.data || !props.data.product.RecordId) {
        // No product exists, don't bother showing out of stock
        return false;
    }

    const hasAvailableProducts = props.hasAvailableProducts ?? true;
    if (!hasAvailableProducts) {
        return true;
    }

    if (props.data.product.Dimensions) {
        if (props.data.product.Dimensions.find(dimension => !(dimension.DimensionValue && dimension.DimensionValue.Value))) {
            // At least one dimension with no value exists on the product, so also don't show out of stock
            return false;
        }
    }

    const includedQuantityNumber = includeCurrentQuantity && props.quantity ? props.quantity : defaultQuantity;

    return !(
        props.productAvailability &&
        props.productAvailability.AvailableQuantity !== undefined &&
        props.productAvailability.AvailableQuantity >= includedQuantityNumber
    );
};

const isIntermediateState = (props: IAddToCartComponentProps): boolean => {
    if (props.data?.product.Dimensions) {
        if (props.data.product.Dimensions.find(dimension => !(dimension.DimensionValue && dimension.DimensionValue.Value))) {
            // At least one dimension with no value exists on the product, so also not in intermediate state
            return false;
        }
    }

    if (!props.isLoading && !props.isUpdatingDimension && !props.isLoadingDeliveryOptions && !props.isUpdatingDeliveryOptions) {
        return false;
    }

    return true;
};

export const AddToCartComponent: React.FunctionComponent<IAddToCartComponentProps> = msdyn365Commerce.createComponentOverride<
    // @ts-expect-error
    IAddtoCartComponent
>('AddToCart', { component: AddToCartFunctionalComponent, ...AddToCartComponentActions });

export default AddToCartComponent;
